import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import { IApiResponseError, IApiState } from '../../../types/api';
import { IRole } from '../../../types/role';
import { RootState } from '../..';
import { getRolesAsync } from './getRolesSlice';

interface ICreateRoleRequest {
  name: string;
  description: string;
  permissions: string[];
}

interface ICreateRoleResponse {
  status?: boolean;
  message?: string;
  data?: IRole;
  errors?: IApiResponseError[];
}

const initialState: IApiState<ICreateRoleResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const createRoleAsync = createAsyncThunk(
  'role/create',
  async (
    values: ICreateRoleRequest,
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      const state = getState() as RootState;

      const accessToken = state.auth.value?.data?.token?.accessToken;

      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const config = {
        headers: {
          Authorization: `Token ${accessToken}`,
          'X-Business-Id': businessId,
        },
      };

      const { data } = await api.post('/roles', values, config);

      dispatch(getRolesAsync());

      return data;
    } catch (err) {
      const error = err as AxiosError<ICreateRoleResponse>;

      return rejectWithValue(error.response?.data);
    }
  }
);

export const roleCreationSlice = createSlice({
  name: 'role/create',
  initialState,
  reducers: {
    resetRoleCreation(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createRoleAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(createRoleAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ICreateRoleResponse;
      })
      .addCase(createRoleAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ICreateRoleResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetRoleCreation } = roleCreationSlice.actions;
export default roleCreationSlice.reducer;
